import React, { memo, useState, useMemo, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { ENTITY_TYPES } from "../../../utils";
import useStableCallback from "../../../hooks/useStableCallback";

const StickyHeaderSx = (entity) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  fontSize: "0.75rem",
  fontWeight: 500,
  paddingRight:
    entity === ENTITY_TYPES.item
      ? "3.6rem"
      : entity === ENTITY_TYPES.department
      ? "0.625rem"
      : "3.45rem",
  marginLeft: entity === ENTITY_TYPES.department ? "0.625rem" : "1.25rem",
  marginBottom: entity === ENTITY_TYPES.department ? "10px" : "0px",
  zIndex: 1,
});

const WordBreakSx = { wordBreak: "break-word" };

const StickyHeader = ({ entityType }) => {
  const [sticky, setSticky] = useState("");

  const isSticky = useStableCallback(() => {
    const scrollTop = window.scrollY;
    const topSpacing =
      entityType === (ENTITY_TYPES.item || ENTITY_TYPES.department) ? 260 : 150;
    const stickyClass = scrollTop >= topSpacing ? "is-sticky-header" : "";
    setSticky(stickyClass);
  });

  useEffect(() => {
    window.addEventListener("scroll", isSticky);
    return () => {
      window.removeEventListener("scroll", isSticky);
    };
  }, [isSticky]);

  const headerRenderer = useMemo(() => {
    if (entityType === ENTITY_TYPES.item) {
      return (
        <>
          <Grid item minWidth="1.563rem"></Grid>
          <Grid item md={1.5} lg={1.5} mr={1}>
            Description
          </Grid>
          <Grid item minWidth="4.245rem" mr={1}></Grid>
          <Grid item md={0.45} lg={0.5} mr={1}>
            SKU#
          </Grid>
          <Grid item md={1.5} lg={2} mr={1}>
            Vendor
          </Grid>
          <Grid item md={0.72} lg={0.6} sx={WordBreakSx} mr={1}>
            Restricted
          </Grid>
          <Grid item md={0.55} lg={0.6} sx={WordBreakSx} mr={1}>
            Hazmat
          </Grid>
          <Grid item md={0.3} lg={0.5} mr={1}>
            Pack
          </Grid>
          <Grid item md={0.3} lg={0.5} mr={1}>
            Qty
          </Grid>
          <Grid item md={1.2} lg={1.2} pl={3} mr={1}>
            Cost
          </Grid>
          <Grid item md={1} lg={1} mr={1}>
            Adj. Cost
          </Grid>
          <Grid item md={1} lg={1} mr={1}>
            Ext. Cost
          </Grid>
          <Grid item minWidth="2.188rem" mr={1.5}>
            Adj. Qty
          </Grid>
        </>
      );
    } else if (entityType === ENTITY_TYPES.department) {
      return (
        <>
          <Grid item minWidth="1.563rem" mr={0.5}></Grid>
          <Grid item xs={2.5} mr={0.5}></Grid>
          <Grid item xs={1.5} mr={0.5}>
            Selected
          </Grid>
          <Grid item xs={1} mr={0.5}>
            Existing
          </Grid>
          <Grid item xs={1} mr={0.5}>
            LF
          </Grid>
          <Grid item xs={1} mr={0.5}>
            Adj. LF
          </Grid>
          <Grid item xs={1} mr={0.5}>
            SKU Qty
          </Grid>
          <Grid item xs={1.5} mr={0.5}>
            Adj. Cost
          </Grid>
          <Grid item xs={1.5}>
            Ext. Cost
          </Grid>
        </>
      );
    } else {
      return (
        <>
          <Grid item minWidth="1.563rem"></Grid>
          <Grid item md={1.8} lg={2.5} mr={0.5}></Grid>
          <Grid item minWidth="4.203rem" mr={0.5}></Grid>
          <Grid item md={0.75} mr={0.5}>
            Region
          </Grid>
          <Grid item md={0.8} mr={0.5}>
            Urbanicity
          </Grid>
          <Grid item md={0.25} lg={0.5} mr={0.5}>
            LF
          </Grid>
          <Grid item minWidth="2.693rem" mr={1}>
            Adj. LF
          </Grid>
          <Grid item md={0.5} lg={0.8} mr={0.5}>
            POG SKUs
          </Grid>
          <Grid item md={0.9} lg={1} mr={0.5}>
            POG Cost
          </Grid>
          <Grid item md={0.6} lg={0.9} mr={0.5}>
            Missing SKUs
          </Grid>
          <Grid item md={0.9} lg={1}>
            Adj. Cost
          </Grid>
        </>
      );
    }
  }, [entityType]);

  return (
    <Box sx={StickyHeaderSx(entityType)}>
      <Grid container>
        <Grid
          item
          xs={12}
          container
          alignItems="center"
          justifyContent="space-between"
          className={sticky}
          pr={
            !!sticky &&
            (entityType === ENTITY_TYPES.item
              ? "3.5rem"
              : entityType === ENTITY_TYPES.department
              ? "2rem"
              : "3.85rem")
          }
        >
          {headerRenderer}
        </Grid>
      </Grid>
    </Box>
  );
};

export default memo(StickyHeader);
